import { AreaChartOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Flex, Form, InputNumber, Modal, Segmented, Space, Switch, Tooltip, Spin, message, Select, Row, Col } from "antd";
import { PaginationProps, SelectProps } from "antd/lib";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { getSegmentsData } from "../../utils";
import {
	LabelText,
	ModalDivider,
	TagContainer,
	AnimatedTag,
	MultiSelect,
	ModalHeader,
	PromoBreakPanel,
	PromoHint,
} from "./StyledComponents";
import type { AdConfig, PromoBreakConfig, PromoBreakLength, PromoFrequencyMode, Segment } from "./types";
import {
	PROMO_BREAKS_DEFAULT,
	PROMO_BREAK_LENGTHS,
	PROMO_FREQUENCY_DEFAULTS,
	PROMO_FREQUENCY_LIMITS,
	describePromoBreakPlan,
	getPaidProgrammingFillerDurationError,
	planPromoBreakLengths,
} from "./utils";

type Props = {
	openConfigureAdsModal: boolean;
	setOpenConfigureAdsModal: React.Dispatch<React.SetStateAction<boolean>>;
	setAdConfig?: (config: AdConfig | null) => void;
	adConfig: AdConfig | null;
	selectedTargetPlatform: string;
	/**
	 * CloudPort feed the schedule is exporting from. Scopes every picker (ads and
	 * both filler lists) to that feed. Optional: this modal is also used without a
	 * feed selection, in which case the lists stay unfiltered.
	 */
	selectedAmagiCloudPortFeed?: string;
	/** Display name for the feed above, used only in the load-failure message. */
	selectedCloudPortFeedName?: string;
};

interface ItemProps {
	label: React.ReactNode | string;
	value: string;
}

const FAST_MIN_ADS_MINUTES_PER_HOUR = 2;
const FAST_MAX_ADS_MINUTES_PER_HOUR = 8;
const FAST_ADS_MINUTES_HELP =
	"Enter 2–8 minutes. This is the per-clock-hour ad cap for the whole day; every ad break counts toward it, including breaks next to filler.";

// Minimum episode runtime before an ad break — the shared Amagi FAST / Marathon
// FAST rule that replaced "at least 2 episode segments between breaks". A break
// may occur only once this much episode content has aired since the previous
// one; consecutive breaks are allowed whenever that and the hourly cap both hold.
const FAST_DEFAULT_MIN_EPISODE_MINUTES = 7;
const FAST_MIN_MIN_EPISODE_MINUTES = 1;
const FAST_MAX_MIN_EPISODE_MINUTES = 30;
const FAST_MIN_EPISODE_MINUTES_HELP =
	"Minutes of episode content that must air before an ad break can occur. Default 7. Applies to both Amagi FAST and Marathon FAST.";

// SEGMENT_FEEDS feed_id for the locally-sliced "Marathon" cue segments. Mirrors
// MARATHON_SEGMENT_FEED_ID in the backend (services/scheduling/marathon_constants).
const MARATHON_SEGMENT_FEED_ID = "10";

// The ad/filler dropdowns must always open BELOW their input. antd flips the
// popup upward when there is not enough room beneath it, which on a short screen
// lands the list on top of the search box you are typing into. Pinning it below
// means we also have to cap the list height ourselves — otherwise a long list
// just runs off the bottom of the screen with no way to reach it.
const DROPDOWN_MAX_LIST_HEIGHT = 400;
const DROPDOWN_MIN_LIST_HEIGHT = 160;
/** Breathing room between the bottom of the dropdown and the viewport edge. */
const DROPDOWN_VIEWPORT_GAP = 24;

const clampFastAdsMinutes = (value: number | null | undefined) => {
	if (value === null || value === undefined) return null;
	return Math.min(
		FAST_MAX_ADS_MINUTES_PER_HOUR,
		Math.max(FAST_MIN_ADS_MINUTES_PER_HOUR, Number(value))
	);
};

// An unset value means "use the default", never "no minimum" — an empty field
// must not silently turn the rule off for a schedule that already exists.
const clampFastMinEpisodeMinutes = (value: number | null | undefined) => {
	const numeric = Number(value);
	if (value === null || value === undefined || !Number.isFinite(numeric)) {
		return FAST_DEFAULT_MIN_EPISODE_MINUTES;
	}
	return Math.min(
		FAST_MAX_MIN_EPISODE_MINUTES,
		Math.max(FAST_MIN_MIN_EPISODE_MINUTES, numeric)
	);
};

/**
 * Read promo settings back off a saved ad_config, tolerating anything a schedule
 * written before this feature (or by hand) might hold. An absent block means
 * "no promo breaks", not "use the defaults enabled" — the defaults only supply
 * the shape of the controls the operator then sees.
 */
const normalizePromoBreaksFromConfig = (
	saved: PromoBreakConfig | null | undefined
): PromoBreakConfig => {
	const lengths = PROMO_BREAK_LENGTHS.filter((length) =>
		(Array.isArray(saved?.lengths) ? saved!.lengths : []).some((n) => Number(n) === length)
	);
	const frequency = Number(saved?.frequency);
	const frequencyMode: PromoFrequencyMode = saved?.frequencyMode === "seconds" ? "seconds" : "breaks";
	return {
		enabled: saved?.enabled === true,
		lengths: lengths.length ? lengths : PROMO_BREAKS_DEFAULT.lengths,
		frequencyMode,
		frequency: Number.isFinite(frequency) && frequency > 0
			? frequency
			: PROMO_FREQUENCY_DEFAULTS[frequencyMode],
	};
};

const ConfigureAds = (props: Props) => {
	const {
		openConfigureAdsModal,
		setOpenConfigureAdsModal,
		setAdConfig,
		selectedTargetPlatform,
		adConfig,
		selectedAmagiCloudPortFeed,
		selectedCloudPortFeedName,
	} = props;
	const [segmentList, setSegmentList] = useState<any[]>([]);
	// Both filler pickers (Paid Programming Filler and Other Filler) are backed by
	// segments of type "filler" — not the "ad"/"media" types used for Select Ads.
	const [fillerSegmentList, setFillerSegmentList] = useState<any[]>([]);
	const [value, setValue] = useState<string[]>([]);
	const [fillerValue, setFillerValue] = useState<string[]>([]);
	const [otherFillerValue, setOtherFillerValue] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [pagination, setPagination] = useState<PaginationProps>({});
	const [fillerPagination, setFillerPagination] = useState<PaginationProps>({});
	const [selectedAds, setSelectedAds] = useState<any[]>([]);
	const [selectedFillers, setSelectedFillers] = useState<any[]>([]);
	const [selectedOtherFillers, setSelectedOtherFillers] = useState<any[]>([]);
	const [removingAds, setRemovingAds] = useState<string | null>(null);
	const [removingFillers, setRemovingFillers] = useState<string | null>(null);
	const [removingOtherFillers, setRemovingOtherFillers] = useState<string | null>(null);
	const [numberOfSegments, setNumberOfSegments] = useState<number | null>(null);
	const [minEpisodeMinutes, setMinEpisodeMinutes] = useState<number | null>(
		FAST_DEFAULT_MIN_EPISODE_MINUTES
	);
	const [program, setProgram] = useState<string | null>(null);
	// Promo breaks (Amagi OTA). Held as plain state rather than Form fields: the
	// Form store is populated from the saved config on open, and "Clear all" has
	// to be able to blank the visible controls without the store reverting them
	// (see the note on handleReset).
	const [promoBreaksEnabled, setPromoBreaksEnabled] = useState<boolean>(PROMO_BREAKS_DEFAULT.enabled);
	const [promoLengths, setPromoLengths] = useState<PromoBreakLength[]>(PROMO_BREAKS_DEFAULT.lengths);
	const [promoFrequencyMode, setPromoFrequencyMode] = useState<PromoFrequencyMode>(
		PROMO_BREAKS_DEFAULT.frequencyMode
	);
	const [promoFrequency, setPromoFrequency] = useState<number | null>(PROMO_BREAKS_DEFAULT.frequency);
	const [form] = Form.useForm();
	const lastPopulatedAdConfigRef = useRef<string | null>(null);
	const lastSelectedTargetPlatformRef = useRef<string>(selectedTargetPlatform);
	const isPopulatingFromAdConfigRef = useRef<boolean>(false);
	const adConfigRef = useRef<AdConfig | null>(adConfig);
	adConfigRef.current = adConfig;

	const isAmagiOta = selectedTargetPlatform === "Amagi OTA";
	const isMarathonFast = selectedTargetPlatform === "Marathon FAST";
	const isFastSelected = selectedTargetPlatform === "Amagi FAST" || isMarathonFast;
	// Other Filler is what closes the day to exactly 24h on both FAST pipelines,
	// so Amagi FAST needs the picker too (it previously had none).
	const showOtherFiller = isAmagiOta || isFastSelected;
	// Any platform that renders a filler picker needs the "filler" segment list.
	const needsFillerSegments = showOtherFiller || isAmagiOta;
	// Every picker (ads and both filler lists) is scoped to the feed the schedule
	// exports from. Marathon FAST reads the locally-sliced Marathon cue feed rather
	// than the selected CloudPort feed, matching how the backend picks episodes.
	// An empty feed (page-load window, or a saved feed that no longer resolves)
	// leaves the lists unfiltered rather than querying FEED_ID = ''.
	const segmentFeedId = isMarathonFast ? MARATHON_SEGMENT_FEED_ID : selectedAmagiCloudPortFeed;
	const feedFilter = segmentFeedId ? { segmentFeed: segmentFeedId } : {};
	const otherFillerLabel = isAmagiOta
		? "Other Filler – Sprinkled throughout the day to make sure Paid Programming starts exactly on time."
		: "Other Filler – Sprinkled throughout the day to make sure the day totals exactly 24 hours";

	// What the scheduler will actually build from the current promo settings, so
	// the hint under the frequency input and the Save-time check agree with it.
	const promoBreakDraft: PromoBreakConfig = {
		enabled: promoBreaksEnabled,
		lengths: [...promoLengths].sort((a, b) => a - b),
		frequencyMode: promoFrequencyMode,
		frequency: Number(promoFrequency ?? 0),
	};
	const promoBreakPlan = planPromoBreakLengths(promoBreakDraft);
	const promoFrequencyLimits = PROMO_FREQUENCY_LIMITS[promoFrequencyMode];

	// Include saved DB selections so Select options/tags still show when the item
	// is missing from the current fetch (pagination, feed filter, etc.)
	const uniqueSegments = Array.from(
		new Map(
			[
				...segmentList,
				...(adConfig?.selectedAds || []),
			]
				.filter((item: any) => item?.title)
				.map((item: any) => [item.title, item])
		).values()
	);

	const uniqueAdsOptions = uniqueSegments.map((item: any) => ({
		label: item.title,
		value: item.title,
	}));

	// Filler segments for Paid Programming Filler, plus any saved selections so DB picks always show
	const uniqueFillerSegments = Array.from(
		new Map(
			[...fillerSegmentList, ...(adConfig?.paidProgrammingFillers || [])]
				.filter((item: any) => item?.title)
				.map((item: any) => [item.title, item])
		).values()
	);
	const uniqueFillerOptions = uniqueFillerSegments.map((item: any) => ({
		label: item.title,
		value: item.title,
	}));

	// Filler segments for Other Filler, plus any saved selections so DB picks always show
	const uniqueOtherFillerSegments = Array.from(
		new Map(
			[...fillerSegmentList, ...(adConfig?.otherFillers || [])]
				.filter((item: any) => item?.title)
				.map((item: any) => [item.title, item])
		).values()
	);
	const uniqueOtherFillerOptions = uniqueOtherFillerSegments.map((item: any) => ({
		label: item.title,
		value: item.title,
	}));

	const tagColors = [
		"magenta",
		"red",
		"volcano",
		"orange",
		"gold",
		"lime",
		"green",
		"cyan",
		"blue",
		"geekblue",
		"purple",
	];

	const handleRemoveTag = (tagValue: string) => {
		setRemovingAds(tagValue);
		setTimeout(() => {
			setValue(value.filter(v => v !== tagValue));
			setRemovingAds(null);
		}, 300);
	};

	const handleRemoveFillerTag = (tagValue: string) => {
		setRemovingFillers(tagValue);
		setTimeout(() => {
			setFillerValue(fillerValue.filter(v => v !== tagValue));
			setRemovingFillers(null);
		}, 300);
	};

	const handleRemoveOtherFillerTag = (tagValue: string) => {
		setRemovingOtherFillers(tagValue);
		setTimeout(() => {
			setOtherFillerValue(otherFillerValue.filter(v => v !== tagValue));
			setRemovingOtherFillers(null);
		}, 300);
	};

	const renderTag = (item: ItemProps, onClose: (value: string) => void, removingKey: string | null) => {
		const colorIndex =
			Math.abs(
				item.value
					.toString()
					.split("")
					.reduce((acc, char) => acc + char.charCodeAt(0), 0)
			) % tagColors.length;
		return (
			<AnimatedTag
				key={item.value}
				color={tagColors[colorIndex]}
				closable
				className={removingKey === item.value ? "removing" : ""}
				onClose={() => onClose(item.value)}
			>
				{item.label}
			</AnimatedTag>
		);
	};

	const [dropdownListHeight, setDropdownListHeight] = useState(DROPDOWN_MAX_LIST_HEIGHT);

	// Shrink the list to whatever room is left under the input it belongs to, so
	// pinning the popup downwards can never push it past the viewport edge.
	const fitDropdownBelowInput = (open: boolean) => {
		if (!open) return;
		// Opening a Select focuses its inner search input, so activeElement tells us
		// which of the three dropdowns was just opened without threading a ref
		// through each one. rAF lets that focus land first.
		requestAnimationFrame(() => {
			const selectEl = (document.activeElement as HTMLElement | null)?.closest(".ant-select");
			const spaceBelow = selectEl
				? window.innerHeight - selectEl.getBoundingClientRect().bottom - DROPDOWN_VIEWPORT_GAP
				: window.innerHeight * 0.4;
			setDropdownListHeight(
				Math.max(DROPDOWN_MIN_LIST_HEIGHT, Math.min(DROPDOWN_MAX_LIST_HEIGHT, spaceBelow))
			);
		});
	};

	const sharedProps: SelectProps = {
		mode: "multiple",
		style: { width: "100%" },
		maxTagCount: 0,
		showArrow: true,
		removeIcon: null,
		tagRender: () => <span style={{ display: "none" }} />,
		tokenSeparators: [],
		dropdownMatchSelectWidth: false,
		virtual: false,
		placement: "bottomLeft",
		// adjustY: false is what actually stops the upward flip. adjustX stays on so
		// the popup (wider than the input) can still shift to stay on screen.
		dropdownAlign: { overflow: { adjustY: false } },
		listHeight: dropdownListHeight,
		onDropdownVisibleChange: fitDropdownBelowInput,
	};

	const resolveFromSegmentList = (titles: string[], sourceList: any[] = uniqueSegments): Segment[] =>
		sourceList.filter((item: Segment) =>
			item.title && titles.includes(item.title)
		).filter((item: Segment) => {
			return item.id && item.title && item.asset_identifier && item.segment_type;
		});

	const persistAdConfig = (config: AdConfig | null) => {
		setAdConfig?.(config);
		setOpenConfigureAdsModal(false);
	};

	async function handleSubmit() {
		if (!setAdConfig) {
			setOpenConfigureAdsModal(false);
			return;
		}

		// An empty ad list is a valid, intentional state: "Clear all" + Save must
		// propagate the removal (setAdConfig(null)) rather than being blocked by the
		// required-field validation. Only a *partially* filled form is an error.
		// Use the visible `value` state (the controlled ads select) as the source of
		// truth for "is it cleared?" — the form store can lag/reset out of sync.
		const currentAds: string[] = value ?? [];
		if (!currentAds.length) {
			persistAdConfig(null);
			return;
		}

		try {
			const values = await form.validateFields();

			if (values.numberOfSegments === null || values.numberOfSegments === undefined) {
				message.error(
					isFastSelected
						? "Please select at least one ad and specify minutes of ads per hour"
						: "Please select at least one ad and specify number of segments before ad break"
				);
				return;
			}

			const selectedAdObject = resolveFromSegmentList(values.selectedAds);
			if (selectedAdObject.length === 0) {
				message.error("Selected ads are not valid. Please reselect ads.");
				return;
			}

			if (isAmagiOta && promoBreaksEnabled) {
				if (promoLengths.length === 0) {
					message.error("Pick at least one promo break length (15 s, 30 s, or both).");
					return;
				}
				if (promoBreakPlan.length === 0) {
					message.error(
						promoFrequencyMode === "seconds"
							? `${promoBreakDraft.frequency} seconds per hour is less than the shortest promo break you selected.`
							: "Enter how many promo breaks you want per hour."
					);
					return;
				}
			}

			const paidProgrammingFillers = isAmagiOta
				? resolveFromSegmentList(values.paidProgrammingFillers || [], uniqueFillerSegments)
				: [];
			const nextAdConfig: AdConfig = {
				selectedAds: selectedAdObject,
				...(isFastSelected
					? {
							minutesOfAdsPerHour: Number(values.numberOfSegments),
							minEpisodeMinutesBeforeAdBreak: clampFastMinEpisodeMinutes(
								values.minEpisodeMinutesBeforeAdBreak
							),
						}
					: { numberOfSegments: Number(values.numberOfSegments) }),
				program: values.program ?? null,
				...(isAmagiOta ? { paidProgrammingFillers, promoBreaks: promoBreakDraft } : {}),
				...(showOtherFiller
					? {
							otherFillers: resolveFromSegmentList(
								values.otherFillers || [],
								uniqueOtherFillerSegments
							),
						}
					: {}),
			};

			const fillerDurationError = isAmagiOta
				? getPaidProgrammingFillerDurationError(paidProgrammingFillers)
				: null;

			if (fillerDurationError) {
				Modal.confirm({
					title: "Paid Programming Filler",
					content: fillerDurationError,
					okText: "OK",
					cancelText: "Cancel",
					onOk: () => persistAdConfig(nextAdConfig),
				});
				return;
			}

			persistAdConfig(nextAdConfig);
		} catch (errorInfo) {
			message.error("Please fill in all required fields");
		}
	}

	const handleReset = () => {
		// NOTE: don't use form.resetFields() here — the Form.Items declare
		// initialValue={value}/{numberOfSegments}/…, so resetFields() would reset the
		// form store back to the *saved* config (populated on open), leaving the visible
		// selects empty but the form store still holding the old ads. Save would then
		// read the stale form store and persist the old data. Explicitly blank the store.
		form.setFieldsValue({
			selectedAds: [],
			paidProgrammingFillers: [],
			otherFillers: [],
			numberOfSegments: null,
			minEpisodeMinutesBeforeAdBreak: FAST_DEFAULT_MIN_EPISODE_MINUTES,
			program: null,
		});
		setValue([]);
		setFillerValue([]);
		setOtherFillerValue([]);
		setSelectedAds([]);
		setSelectedFillers([]);
		setSelectedOtherFillers([]);
		setNumberOfSegments(null);
		setMinEpisodeMinutes(FAST_DEFAULT_MIN_EPISODE_MINUTES);
		setProgram(null);
		setPromoBreaksEnabled(PROMO_BREAKS_DEFAULT.enabled);
		setPromoLengths(PROMO_BREAKS_DEFAULT.lengths);
		setPromoFrequencyMode(PROMO_BREAKS_DEFAULT.frequencyMode);
		setPromoFrequency(PROMO_BREAKS_DEFAULT.frequency);
	};

	useEffect(() => {
		if (isPopulatingFromAdConfigRef.current) {
			return;
		}

		if (!segmentList.length || !value.length) {
			return;
		}

		form.setFieldValue("selectedAds", value);
		form.setFieldValue("numberOfSegments", numberOfSegments);
		form.setFieldValue("minEpisodeMinutesBeforeAdBreak", minEpisodeMinutes);
		form.setFieldValue("paidProgrammingFillers", fillerValue);
		form.setFieldValue("otherFillers", otherFillerValue);

		const selectedSegmentObjects = resolveFromSegmentList(value, uniqueSegments);
		// Keep DB-restored tags visible even if segment list has not matched yet
		const adTags = selectedSegmentObjects.length
			? selectedSegmentObjects
			: (adConfigRef.current?.selectedAds || []).filter((item) =>
				item?.title && value.includes(item.title)
			);
		setSelectedAds(adTags.map(item => ({ label: item.title, value: item.title })));

		const selectedFillerObjects = resolveFromSegmentList(fillerValue, uniqueFillerSegments);
		const fillerTags = selectedFillerObjects.length
			? selectedFillerObjects
			: (adConfigRef.current?.paidProgrammingFillers || []).filter((item) =>
				item?.title && fillerValue.includes(item.title)
			);
		setSelectedFillers(fillerTags.map(item => ({ label: item.title, value: item.title })));

		const selectedOtherFillerObjects = resolveFromSegmentList(otherFillerValue, uniqueOtherFillerSegments);
		// Keep DB-restored tags visible even if filler list has not finished loading
		const otherFillerTags = selectedOtherFillerObjects.length
			? selectedOtherFillerObjects
			: (adConfigRef.current?.otherFillers || []).filter((item) =>
				item?.title && otherFillerValue.includes(item.title)
			);
		setSelectedOtherFillers(otherFillerTags.map(item => ({ label: item.title, value: item.title })));

		// NOTE: This effect only keeps the visible tag chips in sync with the
		// in-modal selection. It intentionally does NOT push to the parent
		// (setAdConfig) — that happens only when the user clicks Save
		// (handleSubmit). Auto-pushing here made Cancel/Close unable to discard
		// edits and made "Clear all" silently fail to propagate.
	}, [value, fillerValue, otherFillerValue, segmentList, fillerSegmentList, numberOfSegments, minEpisodeMinutes, program, selectedTargetPlatform, isAmagiOta, showOtherFiller, isFastSelected, form]);

	useEffect(() => {
		if (lastSelectedTargetPlatformRef.current !== selectedTargetPlatform) {
			handleReset();
			lastPopulatedAdConfigRef.current = null;
			lastSelectedTargetPlatformRef.current = selectedTargetPlatform;
		}
	}, [selectedTargetPlatform]);

	useEffect(() => {
		const noopLoading: React.Dispatch<React.SetStateAction<boolean>> = () => undefined;
		// The feed changes more than once while the page settles (no feed, then the
		// first CloudPort feed, then the saved schedule's feed), so several fetches
		// can be in flight at once. The unfiltered one is the largest and slowest,
		// so without this guard it can resolve last and overwrite the list that was
		// correctly scoped to the feed the user is actually on.
		let cancelled = false;
		const ifCurrent = <T,>(
			apply: React.Dispatch<React.SetStateAction<T>>
		): React.Dispatch<React.SetStateAction<T>> => (value) => {
			if (!cancelled) apply(value);
		};
		setIsLoading(true);
		// Surface a failed fetch instead of silently rendering an empty picker.
		// Saved ads still show (they're merged into the options from adConfig),
		// but the user needs to know the fresh list didn't load.
		let fetchFailed = false;
		const onFetchError = (err: any) => {
			fetchFailed = true;
			console.error("Failed to load ad segment list", err);
		};
		(async function fetchAdsList() {
			try {
				if (selectedTargetPlatform !== "Wurl") {
					await getSegmentsData(
						ifCurrent(setSegmentList),
						noopLoading,
						pagination,
						ifCurrent(setPagination),
						{
							segmentType: isMarathonFast || selectedTargetPlatform === "Amagi FAST" ? "ad" : "media",
							...feedFilter,
						},
						onFetchError
					);

					// One "filler" fetch feeds both filler pickers — Paid Programming
					// Filler (Amagi OTA) and Other Filler (Amagi OTA / Amagi FAST /
					// Marathon FAST) — and is scoped to the same feed as the ads so
					// operators only see their own channel's fillers.
					if (needsFillerSegments) {
						await getSegmentsData(
							ifCurrent(setFillerSegmentList),
							noopLoading,
							fillerPagination,
							ifCurrent(setFillerPagination),
							{
								segmentType: "filler",
								...feedFilter,
							},
							onFetchError
						);
					} else {
						ifCurrent(setFillerSegmentList)([]);
					}
				} else {
					ifCurrent(setFillerSegmentList)([]);
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false);
					if (fetchFailed) {
						const target = selectedCloudPortFeedName
							? `${selectedTargetPlatform} (${selectedCloudPortFeedName})`
							: selectedTargetPlatform;
						message.error(
							`Could not load the ads list for ${target}. Your saved ads are still shown; reopen Configure Ads to retry loading the full list.`
						);
					}
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [selectedTargetPlatform, needsFillerSegments, segmentFeedId]);

	// Dedupe key so we don't repopulate the form on every render for the same config.
	const adConfigKeyOf = (config: AdConfig): string => {
		const adPacingValue = isFastSelected
			? clampFastAdsMinutes(config.minutesOfAdsPerHour ?? config.numberOfSegments ?? null)
			: (config.numberOfSegments ?? null);
		return JSON.stringify({
			selectedAds: (config.selectedAds?.map(ad => ad.title).filter(Boolean) || []).sort(),
			paidProgrammingFillers: (config.paidProgrammingFillers?.map(ad => ad.title).filter(Boolean) || []).sort(),
			otherFillers: (config.otherFillers?.map(ad => ad.title).filter(Boolean) || []).sort(),
			numberOfSegments: adPacingValue,
			minEpisodeMinutesBeforeAdBreak: isFastSelected
				? clampFastMinEpisodeMinutes(config.minEpisodeMinutesBeforeAdBreak)
				: null,
			program: config.program ?? null,
			promoBreaks: isAmagiOta ? normalizePromoBreaksFromConfig(config.promoBreaks) : null,
		});
	};

	// Populate the in-modal form/tags from a saved ad_config. Reads only from the
	// config's own objects (which carry title/id), so it works even when the async
	// segment fetch is slow, empty, or failed — fixing the "loaded blank" bug.
	const applyAdConfigToForm = (config: AdConfig) => {
		const adPacingValue = isFastSelected
			? clampFastAdsMinutes(config.minutesOfAdsPerHour ?? config.numberOfSegments ?? null)
			: (config.numberOfSegments ?? null);
		const minEpisodeMinutesValue = isFastSelected
			? clampFastMinEpisodeMinutes(config.minEpisodeMinutesBeforeAdBreak)
			: null;
		const selectedAdTitles = config.selectedAds?.map(ad => ad.title).filter(Boolean) || [];
		const fillerTitles = config.paidProgrammingFillers?.map(ad => ad.title).filter(Boolean) || [];
		const savedOtherFillers = (config.otherFillers || []).filter((ad) => ad?.title);
		const otherFillerTitles = savedOtherFillers.map(ad => ad.title);

		const savedSelectedAds = (config.selectedAds || []).filter((ad) => ad?.title);
		setSelectedAds(savedSelectedAds.map(item => ({ label: item.title, value: item.title })));
		setValue(selectedAdTitles);

		const savedFillers = (config.paidProgrammingFillers || []).filter((ad) => ad?.title);
		setSelectedFillers(savedFillers.map(item => ({ label: item.title, value: item.title })));
		setFillerValue(fillerTitles);

		setSelectedOtherFillers(savedOtherFillers.map(item => ({ label: item.title, value: item.title })));
		setOtherFillerValue(otherFillerTitles);

		setNumberOfSegments(adPacingValue);
		setMinEpisodeMinutes(minEpisodeMinutesValue ?? FAST_DEFAULT_MIN_EPISODE_MINUTES);
		setProgram(config.program);

		const savedPromoBreaks = normalizePromoBreaksFromConfig(config.promoBreaks);
		setPromoBreaksEnabled(savedPromoBreaks.enabled);
		setPromoLengths(savedPromoBreaks.lengths);
		setPromoFrequencyMode(savedPromoBreaks.frequencyMode);
		setPromoFrequency(savedPromoBreaks.frequency);

		form.setFieldsValue({
			selectedAds: selectedAdTitles,
			paidProgrammingFillers: fillerTitles,
			otherFillers: otherFillerTitles,
			numberOfSegments: adPacingValue,
			minEpisodeMinutesBeforeAdBreak: minEpisodeMinutesValue ?? FAST_DEFAULT_MIN_EPISODE_MINUTES,
			program: config.program,
		});
	};

	// Restore form from saved ad_config (including Other Filler from DB).
	// Independent of the segment fetch — saved objects carry everything we need.
	useEffect(() => {
		if (adConfig) {
			const adConfigKey = adConfigKeyOf(adConfig);
			if (lastPopulatedAdConfigRef.current !== adConfigKey) {
				isPopulatingFromAdConfigRef.current = true;
				applyAdConfigToForm(adConfig);
				lastPopulatedAdConfigRef.current = adConfigKey;
			}
		} else if (lastPopulatedAdConfigRef.current !== null) {
			handleReset();
			lastPopulatedAdConfigRef.current = null;
		}
	}, [adConfig, isFastSelected, form]);

	// Cancel/Close discards in-modal edits by re-syncing the draft to the last
	// saved config (or clearing it when there is none) before closing.
	const discardDraftAndClose = () => {
		if (adConfig) {
			isPopulatingFromAdConfigRef.current = true;
			applyAdConfigToForm(adConfig);
			lastPopulatedAdConfigRef.current = adConfigKeyOf(adConfig);
		} else {
			handleReset();
			lastPopulatedAdConfigRef.current = null;
		}
		setOpenConfigureAdsModal(false);
	};

	useEffect(() => {
		if (isPopulatingFromAdConfigRef.current) {
			isPopulatingFromAdConfigRef.current = false;
		}
	});

	const isWurlSelected = useMemo(() => {
		if (!selectedTargetPlatform) {
			return true;
		}
		return selectedTargetPlatform.trim() === "Wurl";
	}, [selectedTargetPlatform]);

	return (
		<React.Fragment>
			{isWurlSelected ? (
				<Tooltip placement="top" title="Not applicable for Wurl" color="geekblue">
					<Button
						type="default"
						onClick={() => setOpenConfigureAdsModal(true)}
						style={{ color: "#8e8e8e" }}
						disabled
					>
						<AreaChartOutlined /> Configure Ads
					</Button>
				</Tooltip>
			) : (
				<Button
					type="default"
					onClick={() => setOpenConfigureAdsModal(true)}
					style={{ color: "#515151" }}
				>
					<AreaChartOutlined /> Configure Ads
				</Button>
			)}
			<Modal
				width={700}
				open={openConfigureAdsModal}
				onOk={handleSubmit}
				onCancel={discardDraftAndClose}
				title={
					<ModalHeader>
						<AreaChartOutlined />
						Configure Ads
					</ModalHeader>
				}
				footer={() => (
					<Flex justify="space-between">
						<div>
							<Button onClick={handleReset} disabled={isLoading}>
								Clear all
							</Button>
						</div>
						<Flex gap={8}>
							<Button onClick={discardDraftAndClose} disabled={isLoading}>
								Close
							</Button>
							<Button type="primary" onClick={handleSubmit} disabled={isLoading}>
								Save
							</Button>
						</Flex>
					</Flex>
				)}
			>
				<ModalDivider />
				<Spin
					spinning={isLoading}
					tip={<p style={{ backgroundColor: 'white' }}>Fetching ads for {selectedTargetPlatform}...</p>}
					indicator={<LoadingOutlined spin />}
					size="large"
				>
					<Form form={form} onFinish={handleSubmit}>
						<Space direction="vertical" size={"middle"} style={{ width: "100%", margin: "20px 0" }}>
							{selectedAds.length ? (
								<TagContainer hasItems={selectedAds.length > 0}>
									{selectedAds.map((item) => renderTag(item, handleRemoveTag, removingAds))}
								</TagContainer>
							) : null}
							<div>
								<LabelText>Select Ads</LabelText>
								<Form.Item
									name={"selectedAds"}
									initialValue={value}
									rules={[
										{
											required: true,
											message: "At least one ad must be selected.",
										},
									]}
									style={{ marginBottom: 0 }}
								>
									<MultiSelect
										{...sharedProps}
										options={uniqueAdsOptions}
										value={value}
										onChange={setValue}
										placeholder="Select Ads..."
									/>
								</Form.Item>
							</div>
							{isAmagiOta && (
								<div>
									{selectedFillers.length ? (
										<TagContainer hasItems={selectedFillers.length > 0}>
											{selectedFillers.map((item) =>
												renderTag(item, handleRemoveFillerTag, removingFillers)
											)}
										</TagContainer>
									) : null}
									<LabelText>Paid Programming Filler – Used in between paid spots to round them 30 minutes</LabelText>
									<Form.Item
										name={"paidProgrammingFillers"}
										initialValue={fillerValue}
										style={{ marginBottom: 0 }}
									>
										<MultiSelect
											{...sharedProps}
											options={uniqueFillerOptions}
											value={fillerValue}
											onChange={setFillerValue}
											placeholder="Select Paid Programming Filler..."
										/>
									</Form.Item>
								</div>
							)}
							{showOtherFiller && (
								<div>
									{selectedOtherFillers.length ? (
										<TagContainer hasItems={selectedOtherFillers.length > 0}>
											{selectedOtherFillers.map((item) =>
												renderTag(item, handleRemoveOtherFillerTag, removingOtherFillers)
											)}
										</TagContainer>
									) : null}
									<LabelText>{otherFillerLabel}</LabelText>
									<Form.Item
										name={"otherFillers"}
										initialValue={otherFillerValue}
										style={{ marginBottom: 0 }}
									>
										<MultiSelect
											{...sharedProps}
											options={uniqueOtherFillerOptions}
											value={otherFillerValue}
											onChange={setOtherFillerValue}
											placeholder="Select Other Filler..."
										/>
									</Form.Item>
								</div>
							)}
							{isAmagiOta && (
								<PromoBreakPanel>
									<Flex gap={12} align="flex-start">
										<Switch
											checked={promoBreaksEnabled}
											onChange={setPromoBreaksEnabled}
											aria-label="Include promo breaks"
										/>
										<div>
											<LabelText inline>Include promo breaks</LabelText>
											<PromoHint>
												Adds promo breaks to the generated schedule alongside ads and filler.
											</PromoHint>
										</div>
									</Flex>
									{promoBreaksEnabled && (
										<Space direction="vertical" size={16} style={{ width: "100%", marginTop: 16 }}>
											<div>
												<LabelText>Promo break length</LabelText>
												<Flex gap={24} wrap="wrap">
													{PROMO_BREAK_LENGTHS.map((length) => (
														<Checkbox
															key={length}
															checked={promoLengths.includes(length)}
															onChange={(event) =>
																setPromoLengths((current) =>
																	event.target.checked
																		? [...current, length].sort((a, b) => a - b)
																		: current.filter((item) => item !== length)
																)
															}
														>
															{length}-second breaks
														</Checkbox>
													))}
												</Flex>
												<PromoHint>
													{promoLengths.length > 1
														? "Breaks alternate between the selected lengths, shortest first. A length with no promo of exactly that duration in the feed is dropped, and the rest still cover the hour's breaks."
														: "Pick one length or both."}
												</PromoHint>
											</div>
											<div>
												<LabelText>Promo frequency</LabelText>
												<Flex gap={8} align="center" wrap="wrap">
													<InputNumber
														style={{ width: 92 }}
														min={promoFrequencyLimits.min}
														max={promoFrequencyLimits.max}
														step={promoFrequencyMode === "seconds" ? 15 : 1}
														precision={0}
														value={promoFrequency}
														onChange={(next) => setPromoFrequency(next as number | null)}
													/>
													<span style={{ fontSize: 13, color: "#404040" }}>promo</span>
													<Segmented
														value={promoFrequencyMode}
														onChange={(next) => {
															const mode = next as PromoFrequencyMode;
															// Keep the operator's intent across the unit switch: the
															// same schedule, re-expressed. 4 alternating breaks
															// becomes "90 seconds", and back again.
															const { min, max } = PROMO_FREQUENCY_LIMITS[mode];
															const converted = mode === "seconds"
																? promoBreakPlan.reduce((sum, seconds) => sum + seconds, 0)
																: promoBreakPlan.length;
															setPromoFrequencyMode(mode);
															setPromoFrequency(
																converted > 0
																	? Math.min(max, Math.max(min, converted))
																	: PROMO_FREQUENCY_DEFAULTS[mode]
															);
														}}
														options={[
															{ label: "breaks", value: "breaks" },
															{ label: "seconds", value: "seconds" },
														]}
													/>
													<span style={{ fontSize: 13, color: "#404040" }}>
														per hour of programming
													</span>
												</Flex>
												<PromoHint>{describePromoBreakPlan(promoBreakDraft)}</PromoHint>
											</div>
										</Space>
									)}
								</PromoBreakPanel>
							)}
							<Row gutter={isAmagiOta || isFastSelected ? 16 : 0}>
								<Col span={isAmagiOta || isFastSelected ? 12 : 24}>
									<LabelText>
										{isFastSelected
											? "Minutes of Ads Per Hour"
											: "# Segments before ad break"}
									</LabelText>
									<Form.Item
										name="numberOfSegments"
										initialValue={numberOfSegments}
										help={isFastSelected ? FAST_ADS_MINUTES_HELP : undefined}
										rules={[
											{
												required: true,
												message: isFastSelected
													? "Minutes of ads per hour is required."
													: "Number of segments is required.",
											},
										]}
										style={{ marginBottom: 0 }}
									>
										<InputNumber
											required
											style={{ width: "100%" }}
											min={isFastSelected ? FAST_MIN_ADS_MINUTES_PER_HOUR : 1}
											max={isFastSelected ? FAST_MAX_ADS_MINUTES_PER_HOUR : undefined}
											precision={0}
											onChange={value => setNumberOfSegments(value)}
										/>
									</Form.Item>
								</Col>
								{isFastSelected && (
									<Col span={12}>
										<LabelText>Minimum episode minutes before ad break</LabelText>
										<Form.Item
											name="minEpisodeMinutesBeforeAdBreak"
											initialValue={minEpisodeMinutes ?? FAST_DEFAULT_MIN_EPISODE_MINUTES}
											help={FAST_MIN_EPISODE_MINUTES_HELP}
											rules={[
												{
													required: true,
													message: "Minimum episode minutes before ad break is required.",
												},
											]}
											style={{ marginBottom: 0 }}
										>
											<InputNumber
												required
												style={{ width: "100%" }}
												min={FAST_MIN_MIN_EPISODE_MINUTES}
												max={FAST_MAX_MIN_EPISODE_MINUTES}
												precision={0}
												onChange={value => setMinEpisodeMinutes(value as number | null)}
											/>
										</Form.Item>
									</Col>
								)}
								{isAmagiOta && <Col span={12}>
									<LabelText>Program</LabelText>
									<Form.Item
										name="program"
										rules={[
											{
												required: true,
												message: "Please select a program.",
											}
										]}
										style={{ marginBottom: 0 }}
									>
										<Select
											options={[
												{ value: 'NoseyTV', label: 'NoseyTV' },
												{ value: 'Confess', label: 'Confess' }
											]}
											onChange={value => setProgram(value)}
										/>
									</Form.Item>
								</Col>}
							</Row>
						</Space>
					</Form>
				</Spin>
			</Modal>
		</React.Fragment>
	);
};

export default ConfigureAds;
